import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, NotePencil, PaperPlaneTilt, Trash, EnvelopeSimple, IdentificationCard, MapPin, Phone } from "@phosphor-icons/react";
import { formatInTimeZone } from "date-fns-tz";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { api, formatApiErrorDetail } from "../../lib/api";

const STATUS_STYLE = {
  sent: "text-igc-success border-igc-success/40 bg-igc-success/10",
  failed: "text-igc-danger border-igc-danger/40 bg-igc-danger/10",
  skipped: "text-igc-muted border-igc-gold/20 bg-igc-surfaceLight",
};

function formatNoteTime(note) {
  try {
    return formatInTimeZone(new Date(note.created_at), note.author_time_zone || "America/New_York", "MMM d, yyyy h:mm a");
  } catch {
    return note.created_at;
  }
}

export function LeadRecordModal({ lead, currentUserId, userRole, onClose }) {
  const leadId = lead.id;
  const leadName = lead.name;
  const profile = lead.prospect_profile;
  const [tab, setTab] = useState(profile ? "profile" : "notes");
  const [notes, setNotes] = useState([]);
  const [outreach, setOutreach] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    // Bug fix (Wes, Sep 17 2026): Promise.all meant a failed outreach-log fetch (e.g. no outreach
    // data yet for this lead) silently blocked notes from ever rendering, even though the note
    // saved fine — the rep would type a note, save it, and see nothing. Independent now.
    api.get(`/leads/${leadId}/notes`).then((res) => setNotes(res.data || [])).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get(`/leads/${leadId}/outreach-log`).then((res) => setOutreach(res.data || [])).catch(() => setOutreach([])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [leadId]);

  const addNote = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/leads/${leadId}/notes`, { text: text.trim() });
      setText("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/leads/${leadId}/notes/${noteId}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4" data-testid="lead-record-modal-overlay">
      <div className="bg-igc-surface border border-igc-gold/30 max-w-lg w-full p-6 relative max-h-[85vh] flex flex-col" data-testid="lead-record-modal">
        <button data-testid="lead-record-modal-close-button" onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-igc-muted hover:text-igc-cream">
          <X size={18} />
        </button>
        <p className="font-serif text-xl text-igc-cream mb-4 pr-8">{leadName}</p>

        <div className="flex border-b border-igc-gold/20 mb-4 flex-shrink-0">
          {profile && (
            <button
              data-testid="lead-record-tab-profile"
              onClick={() => setTab("profile")}
              title="Shows this lead's phone numbers, location, and any extra profile data on file."
              className={`py-2 px-3 text-sm font-medium flex items-center gap-1.5 ${tab === "profile" ? "text-igc-goldText border-b-2 border-igc-gold" : "text-igc-muted hover:text-igc-cream"}`}
            >
              <IdentificationCard size={14} weight="duotone" /> Full Profile
            </button>
          )}
          <button
            data-testid="lead-record-tab-notes"
            onClick={() => setTab("notes")}
            title="Internal notes about this lead, visible to your team."
            className={`py-2 px-3 text-sm font-medium flex items-center gap-1.5 ${tab === "notes" ? "text-igc-goldText border-b-2 border-igc-gold" : "text-igc-muted hover:text-igc-cream"}`}
          >
            <NotePencil size={14} weight="duotone" /> Notes
          </button>
          <button
            data-testid="lead-record-tab-outreach"
            onClick={() => setTab("outreach")}
            title="Every automated email/text NATE-R has sent to this lead, and whether it was delivered."
            className={`py-2 px-3 text-sm font-medium flex items-center gap-1.5 ${tab === "outreach" ? "text-igc-goldText border-b-2 border-igc-gold" : "text-igc-muted hover:text-igc-cream"}`}
          >
            <EnvelopeSimple size={14} weight="duotone" /> Nate Outreach Log
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {tab === "profile" && profile && (
            <div data-testid="lead-profile-panel">
              <p className="text-igc-muted text-xs uppercase tracking-widest mb-2 flex items-center gap-1.5"><Phone size={12} /> Phone Numbers</p>
              <div className="space-y-1.5 mb-4">
                {(profile.phones || []).length === 0 && <p className="text-igc-muted text-sm" data-testid="lead-profile-no-phones">No phone numbers on file.</p>}
                {(profile.phones || []).map((p, i) => (
                  <div key={i} className="flex items-center justify-between bg-igc-surfaceLight border border-igc-gold/10 px-3 py-2" data-testid={`lead-profile-phone-${i}`}>
                    <span className="text-igc-cream text-sm">{p.label} — {p.number}</span>
                    {p.recommended && <span className="text-[10px] uppercase px-2 py-0.5 bg-igc-gold/15 text-igc-goldText rounded-full">Recommended</span>}
                  </div>
                ))}
              </div>
              {(profile.phones || []).length > 0 && (
                <p className="text-igc-muted text-[11px] mb-4" data-testid="lead-profile-phones-note">All available numbers shown — start with Mobile, work down the list.</p>
              )}

              {profile.location && (
                <div className="mb-4">
                  <p className="text-igc-muted text-xs uppercase tracking-widest mb-2 flex items-center gap-1.5"><MapPin size={12} /> Location</p>
                  <p className="text-igc-cream text-sm" data-testid="lead-profile-location">{profile.location}</p>
                </div>
              )}

              {(profile.profile_fields || []).length > 0 && (
                <div>
                  <p className="text-igc-muted text-xs uppercase tracking-widest mb-2">Profile Details</p>
                  <div className="grid grid-cols-2 gap-2">
                    {profile.profile_fields.map((f, i) => (
                      <div key={i} className="bg-igc-surfaceLight border border-igc-gold/10 p-2.5" data-testid={`lead-profile-field-${i}`}>
                        <p className="text-igc-muted text-[10px] uppercase tracking-widest">{f.label}</p>
                        <p className="text-igc-cream text-sm">{String(f.value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(profile.phones || []).length === 0 && !profile.location && (profile.profile_fields || []).length === 0 && (
                <p className="text-igc-muted text-sm" data-testid="lead-profile-empty">No additional profile data on file for this lead.</p>
              )}
            </div>
          )}

          {loading && tab !== "profile" && <p className="text-igc-muted text-sm" data-testid="lead-record-loading">Loading...</p>}

          {!loading && tab === "notes" && (
            <div data-testid="lead-notes-panel">
              <div className="flex gap-2 mb-4">
                <Textarea
                  data-testid="lead-note-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Add a note about this lead..."
                  title="Write a note about this lead for your team to see — call outcomes, objections, next steps."
                  className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream min-h-[60px] resize-none"
                />
                <Button data-testid="lead-note-submit-button" onClick={addNote} disabled={saving || !text.trim()} title="Saves this note." className="bg-igc-gold hover:bg-igc-goldHover text-black shrink-0 self-end">
                  <PaperPlaneTilt size={14} weight="fill" />
                </Button>
              </div>
              {notes.length === 0 && <p className="text-igc-muted text-sm" data-testid="lead-notes-empty">No notes yet on this lead.</p>}
              <div className="space-y-2">
                {notes.map((n) => (
                  <div key={n.id} className="bg-igc-surfaceLight border border-igc-gold/10 p-3" data-testid={`lead-note-${n.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-igc-cream text-sm whitespace-pre-wrap flex-1" data-testid={`lead-note-text-${n.id}`}>{n.text}</p>
                      {(userRole === "manager" || userRole === "admin" || n.author_id === currentUserId) && (
                        <button data-testid={`lead-note-delete-${n.id}`} onClick={() => deleteNote(n.id)} title="Permanently deletes this note." className="text-igc-danger/70 hover:text-igc-danger shrink-0" aria-label="Delete note">
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                    <p className="text-igc-muted text-[11px] mt-1.5">
                      {n.author_name} ({n.author_role}) — {formatNoteTime(n)} {n.author_time_zone_label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loading && tab === "outreach" && (
            <div data-testid="lead-outreach-panel">
              {outreach.length === 0 && <p className="text-igc-muted text-sm" data-testid="lead-outreach-empty">No NATE-R outreach sent yet — starts the first time this lead is called.</p>}
              <div className="space-y-2">
                {outreach.map((o) => (
                  <div key={o.id} className="bg-igc-surfaceLight border border-igc-gold/10 p-3" data-testid={`lead-outreach-${o.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-igc-cream text-sm">{o.subject || `Step ${o.step_index + 1}`}</p>
                      <span className={`text-[10px] uppercase px-2 py-0.5 border rounded-full ${STATUS_STYLE[o.status] || STATUS_STYLE.skipped}`} data-testid={`lead-outreach-status-${o.id}`}>{o.status}</span>
                    </div>
                    <p className="text-igc-muted text-[11px] mt-1">{o.sequence_name} — {o.channel} — {new Date(o.sent_at).toLocaleString()}</p>
                    {o.detail && <p className="text-igc-muted text-[11px] mt-0.5">{o.detail}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
